import { DatasetCameraTable } from "./DatasetCameraTable";
import { Text } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import type { DatasetDetails, DatasetStructure, DatasetStructureChannel } from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import { ChannelStructureGroups } from "@/components/dataset/ChannelStructureGroups";
import type { LoadState } from "@/components/dataset/loadState";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import styles from "./DatasetOverviewPanel.module.css";
import { formatArmLayout } from "@/components/filter/datasetFilters";

/** 메타 정보의 기술 영역. 채널 구조와 영상 통계를 전체 폭에 배치한다. */
export type DatasetOverviewPanelProps = {
  state: LoadState<DatasetDetails>;
  structureState: LoadState<DatasetStructure>;
  onDesignateVideoFps?: (fps: number) => Promise<void>;
  videoInfoId?: string;
};

// 채널 구조 카드가 읽는 구조의 부분집합. 카드는 uuid 필드를 쓰지 않는다.
type ChannelStructure = {
  arm_layout: DatasetStructure["arm_layout"];
  episode_count: number;
  channels: readonly DatasetStructureChannel[];
};

export function DatasetOverviewPanel({
  state,
  structureState,
  onDesignateVideoFps,
  videoInfoId,
}: DatasetOverviewPanelProps): ReactNode {
  return (
    <section className={styles.overview} aria-label="기술 정보">
      <ChannelStructureCard structureState={structureState} />
      {state.status === "success" ? (
        <DatasetCameraTable
          id={videoInfoId}
          cameras={state.data.cameras ?? null}
          videoDetails={state.data}
          onDesignateVideoFps={onDesignateVideoFps}
        />
      ) : state.status === "error" ? (
        <EmptyState size="sm" tone="error">
          영상 정보를 불러오지 못했습니다.
        </EmptyState>
      ) : (
        <LoadingState size="sm">영상 정보를 불러오고 있습니다.</LoadingState>
      )}
    </section>
  );
}

// 채널 구조는 차원과 그룹을 보여준다. 영상 해상도는 카메라 표에서만 표시한다.
// 학습 제출의 채널 매핑이 이 구조를 그대로 쓴다. 전체 에피소드보다 적은 에피소드에만 있는
// 채널은 에피소드 수를 함께 표시한다. 카드는 폭 전체(양쪽 열)를 쓴다.
function ChannelStructureCard({ structureState }: { structureState: LoadState<ChannelStructure> }) {
  const structure = structureState.status === "success" ? structureState.data : null;
  return (
    <section className={`${styles.card} ${styles.channelCard}`} aria-label="채널 구조">
      <div className={styles.cardHeader}>
        <Text variant={hatcheryTextModes["caption-bold"]}>채널 구조</Text>
        {structure !== null ? (
          <span className={styles.cardMeta}>
            {formatArmLayout(structure.arm_layout) ?? "미판정"}
          </span>
        ) : null}
      </div>
      {structureState.status === "error" ? (
        <EmptyState size="sm" tone="error">
          채널 구조를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.
        </EmptyState>
      ) : structure === null ? (
        <LoadingState size="sm">채널 구조를 불러오고 있습니다.</LoadingState>
      ) : structure.channels.length === 0 ? (
        <EmptyState size="sm">등록된 채널 구조 정보가 없습니다.</EmptyState>
      ) : (
        <ChannelStructureGroups
          mode="static"
          compact
          channels={structure.channels}
          episodeCount={structure.episode_count}
        />
      )}
    </section>
  );
}
